import { queryOptions, useQuery } from "@tanstack/react-query";
import { ApiError, apiRequest, probeRequest } from "../lib/api";
import { paths } from "../lib/endpoints";
import type { Operator } from "../lib/api-types";

export const meQueryKey = ["me"] as const;

/** The signed-in operator, or null when there is no session. Other failures are rethrown. */
async function fetchOperator(): Promise<Operator | null> {
  try {
    const data = await apiRequest<{ operator: Operator }>(paths.me);
    return data.operator;
  } catch (error) {
    if (error instanceof ApiError && error.code === "unauthorized") {
      return null;
    }
    throw error;
  }
}

export const meQuery = queryOptions({
  queryKey: meQueryKey,
  queryFn: fetchOperator,
  staleTime: 60_000,
  retry: false,
});

export function useMe() {
  return useQuery(meQuery);
}

/**
 * Whether the dev sign-in form should be offered. Dev builds always show it. Elsewhere the
 * route is probed with an empty body: 404 (not mounted) or 403 (not localhost) hides it.
 */
export const devLoginAvailableQuery = queryOptions({
  queryKey: ["dev-login-available"] as const,
  queryFn: async (): Promise<boolean> => {
    if (import.meta.env.DEV) return true;
    const probe = await probeRequest(paths.devLogin, { method: "POST", body: {} });
    return probe.status !== 0 && probe.status !== 404 && probe.code !== "dev_login_forbidden";
  },
  staleTime: Infinity,
  retry: false,
});
